import React, { useEffect, useState } from 'react'
import api from '../services/api'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import { toast } from 'react-toastify'
import { useAuth } from '../context/AuthContext'

const SCOPES = [
  { value: 'global', label: 'Global (Dashboard)' },
  { value: 'dossier', label: 'Dossier' },
  { value: 'negotiation', label: 'Pratica' },
  { value: 'passp_customer', label: 'Clienti Passpartout' }
]

function AiPromptsAdmin() {
  const { user } = useAuth()
  const roleName = (user?.role_name || user?.role || '').toLowerCase()
  const isAdmin = roleName === 'admin'

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [prompts, setPrompts] = useState([])
  const [editing, setEditing] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const isNew = editing && !editing.id

  useEffect(() => {
    if (!isAdmin) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await api.get('/ai/prompts/admin')
        setPrompts(res.data?.prompts || [])
      } catch (e) {
        toast.error('Errore nel caricamento dei prompt AI')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAdmin])

  const startNew = () => {
    setEditing({
      scope: 'global',
      code: '',
      title: '',
      prompt_text: '',
      language: 'it',
      sort_order: 0,
      is_active: true
    })
    setDialogOpen(true)
  }

  const startEdit = (p) => {
    setEditing({
      id: p.id,
      scope: p.scope,
      code: p.code || '',
      title: p.title,
      prompt_text: p.prompt_text,
      language: p.language || 'it',
      sort_order: p.sort_order || 0,
      is_active: p.is_active === 1 || p.is_active === true
    })
    setDialogOpen(true)
  }

  const cancelEdit = () => {
    setEditing(null)
    setDialogOpen(false)
  }

  const handleChange = (field, value) => {
    setEditing((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    if (!editing) return
    try {
      setSaving(true)
      const payload = {
        ...editing,
        sort_order: Number(editing.sort_order) || 0
      }
      const res = await api.post('/ai/prompts', payload)
      const saved = res.data
      setPrompts((prev) => {
        const others = prev.filter((p) => p.id !== saved.id)
        return [...others, saved].sort((a, b) => {
          if (a.scope === b.scope) return a.sort_order - b.sort_order || a.id - b.id
          return a.scope.localeCompare(b.scope)
        })
      })
      setEditing(null)
      setDialogOpen(false)
      toast.success('Prompt AI salvato')
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore nel salvataggio del prompt AI'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo prompt AI?')) return
    try {
      await api.delete(`/ai/prompts/${id}`)
      setPrompts((prev) => prev.filter((p) => p.id !== id))
      if (editing && editing.id === id) {
        setEditing(null)
      }
      toast.success('Prompt AI eliminato')
    } catch (e) {
      toast.error('Errore durante l’eliminazione del prompt AI')
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-2">Prompt AI suggeriti</h1>
        <p className="text-sm text-muted-foreground">Accesso riservato agli amministratori.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Prompt AI suggeriti</h1>
        <Button size="sm" onClick={startNew}>
          Nuovo prompt
        </Button>
      </div>

      <Card className="p-4 space-y-3 min-h-[70vh]">
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            I prompt suggeriti vengono mostrati sopra la chat in base allo scope (Dashboard, Dossier, Pratica, Clienti Passpartout).
          </span>
          <Badge variant="outline" className="text-[10px]">
            Totale: {prompts.length}
          </Badge>
        </div>

        <div className="space-y-1 border rounded-md p-2">
          {prompts.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer"
              onClick={() => startEdit(p)}
            >
              <div className="flex flex-col">
                <span className="text-xs font-medium">
                  [{p.scope}] {p.title}
                </span>
                <span className="text-[10px] text-muted-foreground whitespace-pre-line">
                  {p.prompt_text}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={p.is_active ? 'outline' : 'secondary'} className="text-[9px]">
                  {p.is_active ? 'Attivo' : 'Disattivo'}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[10px] h-6 px-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete(p.id)
                  }}
                >
                  Elimina
                </Button>
              </div>
            </div>
          ))}
          {!prompts.length && (
            <div className="text-[11px] text-muted-foreground px-1 py-2">
              Nessun prompt configurato. Crea un nuovo prompt con il pulsante in alto.
            </div>
          )}
        </div>
      </Card>

      <Dialog
        open={dialogOpen && !!editing}
        onClose={cancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isNew ? 'Nuovo prompt' : `Modifica prompt #${editing?.id}`}
        </DialogTitle>
        {editing && (
          <DialogContent dividers>
            <div className="flex flex-wrap gap-3 mb-3">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="ai-prompt-scope-label">Scope</InputLabel>
                <Select
                  labelId="ai-prompt-scope-label"
                  label="Scope"
                  value={editing.scope}
                  onChange={(e) => handleChange('scope', e.target.value)}
                >
                  {SCOPES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="ai-prompt-lang-label">Lingua</InputLabel>
                <Select
                  labelId="ai-prompt-lang-label"
                  label="Lingua"
                  value={editing.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                >
                  <MenuItem value="it">Italiano</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>

              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">Ordine</span>
                <Input
                  type="number"
                  className="w-20 h-8 text-xs"
                  value={editing.sort_order}
                  onChange={(e) => handleChange('sort_order', e.target.value)}
                />
              </div>

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={editing.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                }
                label={<span className="text-[11px]">Attivo</span>}
              />
            </div>

            <div className="space-y-2">
              <Input
                placeholder="Titolo visibile (es. Sintesi note e task pratica)"
                value={editing.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="text-sm"
              />
              <Input
                placeholder="Codice interno (opzionale, es. PRACTICE_NOTES_TASKS)"
                value={editing.code}
                onChange={(e) => handleChange('code', e.target.value)}
                className="text-xs"
              />
              <textarea
                rows={4}
                className="w-full border rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Testo del prompt che verrà inviato alla chat quando l’utente clicca (in italiano, frase naturale)..."
                value={editing.prompt_text}
                onChange={(e) => handleChange('prompt_text', e.target.value)}
              />
            </div>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={cancelEdit}
            disabled={saving}
          >
            Annulla
          </Button>
          <Button
            size="sm"
            className="text-xs"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AiPromptsAdmin
